/**
 * Email Capture Bar — form handler for [email_capture_bar] shortcode.
 *
 * Binds all .js-email-capture-bar instances on the page.
 * Variants without data-use-ajax post to the Zapier webhook via data-webhook.
 * Variants with data-use-ajax="true" post to the WP AJAX email integrations system.
 *
 * TODO(legacy-email-capture-bar): Delete this file (and its dist/ build)
 * once all [email_capture_bar variant="..."] placements have been migrated
 * to the email_captures CPT ([email_capture id="X"]) via wp-admin. The new
 * captures use assets/js/components/email-capture.js instead.
 *
 * @file email-capture-bar.js
 */
(() => {
  'use strict';

  /**
   * @param {string} val
   * @returns {boolean}
   */
  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val);

  /**
   * @param {HTMLElement} fieldsWrap
   * @param {HTMLElement} thanksEl
   * @param {string}      successMsg
   * @returns {void}
   */
  const showSuccess = (fieldsWrap, thanksEl, successMsg) => {
    fieldsWrap.classList.add('tw-hidden');
    thanksEl.textContent = successMsg;
    thanksEl.classList.remove('tw-hidden');
  };

  /**
   * @param {HTMLElement} submitBtn
   * @param {HTMLElement} errorEl
   * @param {string}      originalLabel
   * @returns {void}
   */
  const showError = (submitBtn, errorEl, originalLabel) => {
    submitBtn.disabled = false;
    submitBtn.removeAttribute('aria-busy');
    submitBtn.textContent = originalLabel;
    errorEl.textContent = 'Something went wrong. Please try again.';
    errorEl.classList.remove('tw-hidden');
  };

  /**
   * Posts to the WP AJAX email integrations endpoint.
   * Fetches a fresh nonce at submit time so cached pages never serve a stale one.
   *
   * @param {string} emailValue
   * @param {string} variant
   * @param {string} honeypotValue
   * @param {string} ajaxUrl
   * @returns {Promise<boolean>} Resolves true on success, false on failure.
   */
  const submitViaAjax = async (emailValue, variant, honeypotValue, ajaxUrl) => {

    const nonceFD = new FormData();
    nonceFD.append('action', 'get_email_capture_bar_nonce');
    const nonceResponse = await fetch(ajaxUrl, { method: 'POST', body: nonceFD });
    const nonceResult   = await nonceResponse.json();

    const subscribeFD = new FormData();
    subscribeFD.append('action', 'email_capture_bar_subscribe');
    subscribeFD.append('nonce', nonceResult.data.nonce);
    subscribeFD.append('email', emailValue);
    subscribeFD.append('variant', variant);
    subscribeFD.append('source_url', window.location.href);
    subscribeFD.append('honeypot_value', honeypotValue);

    const subscribeResponse = await fetch(ajaxUrl, { method: 'POST', body: subscribeFD });
    const subscribeResult   = await subscribeResponse.json();

    return subscribeResult.success === true;
  };

  /**
   * Initializes a single email capture bar instance.
   *
   * @param {HTMLElement} bar - The .js-email-capture-bar container element.
   * @returns {void}
   */
  const initBar = (bar) => {
    const form          = bar.querySelector('form');
    const emailInput    = form.querySelector('input[name="email"]');
    const submitBtn     = form.querySelector('button[type="submit"]');
    const fieldsWrap    = form.querySelector('.js-email-capture-fields');
    const errorEl       = form.querySelector('.js-email-capture-error');
    const thanksEl      = form.querySelector('.js-email-capture-thanks');
    const honeypot      = form.querySelector('input[name="website_url"]');
    const successMsg    = bar.dataset.successMsg || 'Thanks! Check your inbox.';
    const webhook       = bar.dataset.webhook;
    const variant       = bar.dataset.variant;
    const useAjax       = bar.dataset.useAjax === 'true';
    const ajaxUrl       = bar.dataset.ajaxUrl || '';
    const originalLabel = submitBtn.textContent;

    form.addEventListener('submit', async (submitEvent) => {
      submitEvent.preventDefault();

      const emailValue = emailInput.value.trim();

      if (!emailValue || !isValidEmail(emailValue)) {
        emailInput.setAttribute('aria-invalid', 'true');
        errorEl.textContent = 'Please enter a valid email address.';
        errorEl.classList.remove('tw-hidden');
        emailInput.focus();
        return;
      }

      emailInput.removeAttribute('aria-invalid');
      errorEl.classList.add('tw-hidden');
      errorEl.textContent = '';

      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.textContent = 'Submitting…';

      const honeypotValue = honeypot?.value || '';

      if (useAjax) {
        try {
          const succeeded = await submitViaAjax(emailValue, variant, honeypotValue, ajaxUrl);
          if (succeeded) {
            showSuccess(fieldsWrap, thanksEl, successMsg);
          } else {
            showError(submitBtn, errorEl, originalLabel);
          }
        } catch {
          showError(submitBtn, errorEl, originalLabel);
        }
        return;
      }

      /** Honeypot filled — bot detected. Show fake success without posting. */
      if (honeypotValue) {
        showSuccess(fieldsWrap, thanksEl, successMsg);
        return;
      }

      /**
       * mode: 'no-cors' is required for Zapier webhooks — the response
       * is opaque, so success is assumed in .then() and only network
       * errors are caught.
       */
      fetch(webhook, {
        method: 'POST',
        mode: 'no-cors',
        body: JSON.stringify({
          email: emailValue,
          variant,
          source_url: window.location.href,
        }),
      })
        .then(() => {
          showSuccess(fieldsWrap, thanksEl, successMsg);
        })
        .catch(() => {
          showError(submitBtn, errorEl, originalLabel);
        });
    });
  };

  document.querySelectorAll('.js-email-capture-bar').forEach(initBar);
})();
